import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PenTool, Workflow, Compass, LayoutDashboard, Users, Search, Rocket } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../stores';
import Routes from '../routes/Routes';
import { getBrandingColor } from '../utils/brandingStyles';

const ANNOUNCEMENT_ID = 'whats_new_august_2026';
const ALT_ANNOUNCEMENT_IDS = ['famla_collect_beta_may_2026', 'whats_new_may_2026', 'whats_new_may_2026_r2'];
const ALL_ANNOUNCEMENT_IDS = [ANNOUNCEMENT_ID, ...ALT_ANNOUNCEMENT_IDS];
const ANNOUNCEMENT_REV = 1;

interface Feature {
    icon: React.ReactNode;
    title: string;
    description: string;
}

const FeatureItem: React.FC<{ feature: Feature; color: string }> = ({ feature, color }) => (
    <li className="d-flex align-items-start gap-3">
        <span
            className="d-flex align-items-center justify-content-center flex-shrink-0 rounded-circle"
            style={{
                width: 40,
                height: 40,
                background: `${color}22`,
                color,
                marginTop: 2,
            }}
        >
            {feature.icon}
        </span>
        <div>
            <p
                className="mb-1 fw-semibold"
                style={{ fontSize: '0.95rem', color: '#e2e8f0' }}
            >
                {feature.title}
            </p>
            <p
                className="mb-0"
                style={{ fontSize: '0.85rem', lineHeight: 1.55, color: 'rgba(203,213,225,0.7)' }}
            >
                {feature.description}
            </p>
        </div>
    </li>
);

const WhatsNewModal: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const modalRef = useRef<HTMLDivElement>(null);
    const [overlayVisible, setOverlayVisible] = useState(false);
    const [show, setShow] = useState(false);
    const userId = useAppSelector((state) => state.auth.session.user._id);
    const pendingFromStore = useAppSelector((state) => (state.auth.session.user?.pending_announcements || []) as string[]);

    const seenKey = `announcement_seen_${ANNOUNCEMENT_ID}_r${ANNOUNCEMENT_REV}_${userId}`;

    const dismiss = () => {
        const el = modalRef.current;
        if (!el) return;
        const modal = (window as any).bootstrap?.Modal?.getInstance(el);
        modal?.hide();
        localStorage.setItem(seenKey, 'true');
    };

    const handleCta = () => {
        dismiss();
        navigate(`${Routes.HOME}?createDiagram=draw`);
    };

    useEffect(() => {
        if (!userId) return;

        const raw = sessionStorage.getItem('pending_announcements');
        let pending: string[] = raw ? JSON.parse(raw) : [];

        if ((!pending || pending.length === 0) && Array.isArray(pendingFromStore) && pendingFromStore.length > 0) {
            pending = pendingFromStore;
            try {
                sessionStorage.setItem('pending_announcements', JSON.stringify(pendingFromStore));
            } catch {}
        }

        if (!ALL_ANNOUNCEMENT_IDS.some((id) => pending.includes(id))) return;

        if (localStorage.getItem(seenKey) === 'true') return;

        setShow(true);
    }, [userId, pendingFromStore]);

    useEffect(() => {
        if (!show) return;
        const el = modalRef.current;
        if (!el) return;

        const handleShow = () => setOverlayVisible(true);
        const handleHide = () => setOverlayVisible(false);

        el.addEventListener('show.bs.modal', handleShow);
        el.addEventListener('hide.bs.modal', handleHide);

        const modal = (window as any).bootstrap?.Modal?.getOrCreateInstance(el, { keyboard: false });
        modal?.show();

        return () => {
            el.removeEventListener('show.bs.modal', handleShow);
            el.removeEventListener('hide.bs.modal', handleHide);
        };
    }, [show]);

    if (!show) return null;

    const featureColor = getBrandingColor();
    const features: Feature[] = [
        {
            icon: <PenTool size={20} />,
            title: t('whatsNew.canvasTitle'),
            description: t('whatsNew.canvasDesc'),
        },
        {
            icon: <Workflow size={20} />,
            title: t('whatsNew.bpmnTitle'),
            description: t('whatsNew.bpmnDesc'),
        },
        {
            icon: <Compass size={20} />,
            title: t('whatsNew.onboardingTitle'),
            description: t('whatsNew.onboardingDesc'),
        },
        {
            icon: <LayoutDashboard size={20} />,
            title: t('whatsNew.homepageTitle'),
            description: t('whatsNew.homepageDesc'),
        },
        {
            icon: <Users size={20} />,
            title: t('whatsNew.teamControlsTitle'),
            description: t('whatsNew.teamControlsDesc'),
        },
        {
            icon: <Search size={20} />,
            title: t('whatsNew.searchTitle'),
            description: t('whatsNew.searchDesc'),
        },
    ];

    return (
        <>
            {overlayVisible && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: '#000',
                        opacity: 0.85,
                        zIndex: 10001,
                        transition: 'opacity 0.3s ease',
                    }}
                />
            )}

            <div
                ref={modalRef}
                className="modal fade"
                id="whatsNewModal"
                tabIndex={-1}
                aria-labelledby="whatsNewModalLabel"
                aria-hidden="true"
                data-bs-backdrop="false"
                data-bs-keyboard="false"
                style={{ zIndex: 10002 }}
            >
                <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: 600 }}>
                    <div className="modal-content border-0 shadow-lg" style={{ borderRadius: 16, overflow: 'hidden' }}>

                        {/* Header */}
                        <div
                            className="modal-header border-0 pb-0 pt-4 px-5"
                            style={{ background: '#111827' }}
                        >
                            <div className="d-flex align-items-center gap-2">
                                <Rocket size={16} style={{ color: featureColor }} />
                                <span
                                    className="badge rounded-pill px-3 py-1 fw-semibold"
                                    style={{ background: featureColor, color: '#fff', fontSize: '0.82rem', letterSpacing: '0.02em' }}
                                >
                                    {t('whatsNew.badge')}
                                </span>
                                <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.87rem' }}>
                                    {t('whatsNew.period')}
                                </span>
                            </div>
                            <button
                                type="button"
                                className="btn-close btn-close-white"
                                aria-label="Close"
                                onClick={dismiss}
                            />
                        </div>

                        {/* Sub-header */}
                        <div className="px-5 pt-3 pb-4" style={{ background: '#111827' }}>
                            <p
                                id="whatsNewModalLabel"
                                className="mb-0"
                                style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}
                            >
                                {t('whatsNew.subtitle')}
                            </p>
                        </div>

                        {/* Feature list */}
                        <div
                            className="modal-body px-5 py-4"
                            style={{ background: '#1a2130' }}
                        >
                            <ul className="list-unstyled mb-0 d-flex flex-column gap-3">
                                {features.map((f, i) => (
                                    <FeatureItem key={i} feature={f} color={featureColor} />
                                ))}
                            </ul>
                        </div>

                        {/* Footer */}
                        <div className="modal-footer border-0 px-5 pb-5 pt-3 flex-column gap-2" style={{ background: '#1a2130' }}>
                            <p
                                className="mb-1 w-100 text-center"
                                style={{ fontSize: '0.82rem', color: 'rgba(203,213,225,0.55)' }}
                            >
                                {t('whatsNew.collectAvailable')}
                            </p>
                            <button
                                type="button"
                                className="btn w-100 fw-semibold"
                                style={{
                                    background: featureColor,
                                    color: '#fff',
                                    borderRadius: 10,
                                    padding: '12px 0',
                                    fontSize: '0.97rem',
                                    border: 'none',
                                }}
                                onClick={handleCta}
                            >
                                {t('whatsNew.cta')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default WhatsNewModal;
